import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import DateEditor from "@/_components/DateEditor"
import SelectEditor from "@/_components/SelectEditor"
import TextAreaEditor from "@/_components/TextAreaEditor"
import { getLocale, loc } from "@/_services/localization"
import { formatCurrency } from "@/_services/utils"
import "@/_styles/datasheet.module.scss"
import {
  coerceStringToJsNumber,
  formatDecimal,
  formatPercentage,
  getCurrencyDecimals,
  momentDateFormat,
  roundAccurately,
  toCamelCase,
  utcParseDate,
} from "basikon-common-utils"
import get from "lodash.get"
import moment from "moment-timezone"
import React, { createRef } from "react"
import Datasheet from "react-datasheet/lib/DataSheet"

class MathSheetComponent extends React.Component {
  constructor(props) {
    super(props)

    const locale = getLocale()
    this.state = {
      locale,
      dateFormat: momentDateFormat(locale),
    }

    this.componentRef = createRef()
  }

  fixTemplateDatapoints(sheetTemplateDatapoints) {
    sheetTemplateDatapoints?.forEach(datapoint => {
      datapoint.expr = datapoint.formula
      datapoint.name = datapoint.name || toCamelCase(datapoint.label)
      datapoint.regExp = new RegExp("\\b" + datapoint.name + "\\b", "g")
    })
  }

  initState = () => {
    try {
      const { sheetTemplate, datapoints, sheet } = this.props
      if (sheetTemplate) {
        this.fixTemplateDatapoints(sheetTemplate.datapoints || datapoints)
        const grid = this.generateInitialGrid(sheetTemplate, sheet)
        this.setState({ grid })
      } else {
        // not template, just a grid
        const grid = sheet?.grid
        if (Array.isArray(grid)) {
          grid.forEach(line =>
            line.forEach(cell => {
              if (cell.disabled) cell.disableEvents = true
              cell.className = this.getClassName(cell)
            }),
          )
        }

        this.initEditors(grid)
        this.setState({ grid })
      }
    } catch (error) {
      console.log(loc`Error in template: ` + error.message, error)
    }
  }

  componentDidMount() {
    this.initState()
  }

  componentDidUpdate(prevProps) {
    const { obj, sheet, sheetTemplate, readOnly } = this.props
    const { readOnly: prevReadOnly } = prevProps

    if (prevProps.sheetTemplate !== sheetTemplate || prevProps.sheet !== sheet || prevProps.obj !== obj || readOnly !== prevReadOnly) {
      this.initState()
    } else if ((prevProps.sheet && prevProps.sheet.values) !== (sheet && sheet.values)) {
      this.initState()
    } else if (!sheetTemplate && (prevProps.sheet && prevProps.sheet.grid) !== (sheet && sheet.grid)) {
      this.initState()
    }
  }

  generateInitialGrid(sheetTemplate, sheet) {
    let grid = sheet?.grid

    if (sheetTemplate.multipleColumns) {
      // a grid with one record spread on multiple columns such as "retailScoringTemplate"
      grid = this.generateMultipleColumnsGrid(sheetTemplate, grid)
    } else if (sheetTemplate.nbYears) {
      // a grid with N record for N years organized in columns such as "balanceSheetTemplate"
      grid = this.generateMultiYearGrid(sheetTemplate, grid)
    } else {
      // just a grid
      grid = this.generateSimpleGrid(sheetTemplate, grid)
    }
    const values = this.props.sheet?.values
    if (values) {
      grid.forEach(row =>
        row.forEach(cell => {
          if (cell.name && cell.name in values) {
            cell.value = values[cell.name]
            cell.readOnly = true
            cell.className = this.getClassName(cell)
          }
        }),
      )
    }
    this.initEditors(grid)
    return grid
  }

  initEditors(grid) {
    const { obj, readOnly } = this.props
    const globalReadOnly = readOnly || obj?.readOnly

    if (Array.isArray(grid)) {
      for (const line of grid) {
        for (const cell of line) {
          if (!cell) continue

          if (cell.disabled) cell.disableEvents = true
          if (globalReadOnly) cell.readOnly = true

          const format = cell.format || cell.type
          if (format === "date" && !cell.readOnly) {
            cell.dataEditor = DateEditor
          } else if (format === "textarea" && !cell.readOnly) {
            cell.dataEditor = TextAreaEditor
          } else if (cell.select) {
            cell.dataEditor = props => <SelectEditor {...{ ...props, select: cell.select }} />
          }
        }
      }
    }
  }

  generateSimpleGrid(sheetTemplate) {
    const { obj } = this.props
    const sheetTemplateDatapoints = sheetTemplate.datapoints || this.props.datapoints
    if (!sheetTemplateDatapoints) return []

    const grid = []
    for (let i = 0; i < sheetTemplateDatapoints?.length; i++) {
      const line = sheetTemplateDatapoints[i]
      const gridLine = []
      grid.push(gridLine)

      for (let j = 0; j < line.length; j++) {
        const cell = line[j]
        const gridCell = { ...cell }
        gridLine.push(gridCell)
        const cellPositionCss = ` cell-x-${j} cell-y-${i}`
        gridCell.className = this.getClassName(cell) + cellPositionCss

        if (obj && cell.name) {
          const value = get(obj, cell.name)
          gridCell.value = value
          if (gridCell.computeClassName) {
            gridCell.className = this.getClassName(gridCell) + cellPositionCss
          }
        }
      }
    }
    return grid
  }

  generateMultipleColumnsGrid(sheetTemplate, cells) {
    const { obj } = this.props
    const sheetTemplateDatapoints = sheetTemplate.datapoints || this.props.datapoints
    const multipleColumns = sheetTemplate.multipleColumns
    const firstLine = ["", "A"]
    for (let i = 0; i < multipleColumns; i++) {
      firstLine.push(String.fromCharCode("A".charCodeAt(0) + 3 * i + 1))
      firstLine.push(String.fromCharCode("A".charCodeAt(0) + 3 * i + 2))
      if (i < multipleColumns - 1) firstLine.push(String.fromCharCode("B".charCodeAt(0) + 3 * i + 3))
    }
    const currentIndex = []
    for (let i = 0; i < firstLine.length; i++) {
      currentIndex.push(0)
    }
    const datapoints = []
    sheetTemplateDatapoints?.forEach(dp => {
      const column = dp.column || 1
      const keyColumn = 3 * (column - 1) + 2
      currentIndex[column]++

      const keyLine = currentIndex[column]
      const keyColumnChar = String.fromCharCode("A".charCodeAt(0) + keyColumn)
      const key = keyColumnChar + keyLine

      let cell = cells && cells[keyLine][keyColumn]
      cell = { ...cell, ...dp }
      cell.keyColumn = keyColumn
      cell.keyLine = keyLine
      cell.key = key
      cell.className = this.getClassName(cell)

      if (obj && cell.name) {
        const value = get(obj, cell.name)
        cell.value = value
        if (cell.computeClassName) {
          cell.className = this.getClassName(cell)
        }
      }

      dp.key = key

      datapoints.push(cell)
    })

    // fix formula
    datapoints.forEach(datapoint => {
      for (const dp of datapoints) {
        datapoint.expr = datapoint.expr && datapoint.expr.replace(dp.regExp, dp.key)
      }
    })

    let maxLine = 0
    currentIndex.forEach(i => {
      if (i > maxLine) maxLine = i
    })
    const grid = []
    grid.push(
      firstLine.map((col, i) => {
        return {
          readOnly: true,
          width: i === 0 ? "3%" : i % 3 === 1 ? "20%" : i % 3 === 2 ? "10%" : "1%",
          value: col,
        }
      }),
    )
    for (let i = 0; i < maxLine; i++) {
      const line = [{ readOnly: true, value: i + 1 }]
      for (let j = 1; j < firstLine.length; j++) line.push({})
      grid.push(line)
    }
    datapoints.forEach(dp => {
      grid[dp.keyLine][dp.keyColumn - 1] = {
        readOnly: true,
        className: this.getLabelClassName(dp),
        value: dp.label,
      }
      grid[dp.keyLine][dp.keyColumn] = dp
    })
    return grid
  }

  generateMultiYearGrid(sheetTemplate, cells) {
    const sheetTemplateDatapoints = sheetTemplate.datapoints || this.props.datapoints
    const nbYears = sheetTemplate.nbYears || 5

    const firstLine = ["", "A"]
    for (let i = 0; i < nbYears; i++) {
      firstLine.push(String.fromCharCode("B".charCodeAt(0) + i))
    }
    const columnDatapoints = firstLine.map((col, i) => {
      if (i >= 2) {
        // copy
        let datapoints = [...sheetTemplateDatapoints]
        // set key
        for (let j = 0; j < datapoints.length; j++) {
          const key = col + (j + 2)
          let cell = cells && cells[j + 2][i]
          cell = { ...datapoints[j], ...cell }
          cell.key = key
          cell.className = this.getClassName(cell)
          datapoints[j] = cell
        }

        // fix formula
        datapoints = datapoints.map(datapoint => {
          for (const dp of datapoints) {
            datapoint.expr = datapoint.expr && datapoint.expr.replace(dp.regExp, dp.key)
          }
          return datapoint
        })
        return datapoints
      }
      return null
    })

    const grid = []
    grid.push(
      firstLine.map((col, i) => {
        return {
          readOnly: true,
          width: i === 0 ? "3%" : i === 1 ? "30%" : "10%",
          value: col,
        }
      }),
    )

    const secondLine = ["1", ""]
    for (let i = 2; i < 2 + nbYears; i++) {
      secondLine.push("" + new Date().getFullYear() - nbYears - 1 + i)
    }
    grid.push(
      secondLine.map(col => {
        return { readOnly: true, value: col }
      }),
    )

    for (let i = 0; i < sheetTemplateDatapoints.length; i++) {
      const line = [
        { readOnly: true, value: i + 2 },
        {
          readOnly: true,
          className: this.getLabelClassName(sheetTemplateDatapoints[i]),
          value: sheetTemplateDatapoints[i].label,
        },
      ]
      for (let j = 2; j < firstLine.length; j++) {
        const datapoint = columnDatapoints[j][i]
        line.push(datapoint)
      }
      grid.push(line)
    }
    return grid
  }

  getClassName(cell) {
    let cn = cell.labelClassName || ""
    if (cell.expr && cell.expr.startsWith && cell.expr.startsWith("=")) {
      cn += " equation"
    }
    if (cell.format === "date") cn += " cell-date"
    if (cell.select) cn += " cell-select"
    if (cell.totalLevel) cn += " total" + cell.totalLevel
    if (cell.comment) cn += " relative"
    if (cell.comment?.showIconOnCellHover) cn += " cell-comment-hover"
    if (cell.computeClassName) {
      const value = cell.value || (cell.obj && cell.field && cell.obj[cell.field])
      const ccn = cell.computeClassName(value, cell)
      if (ccn) cn += " " + ccn
    }
    return cn.trim()
  }

  getLabelClassName(cell) {
    let cn = cell.labelClassName
    if (cell.hasSeparator) cn += " separator"
    if (cell.hasPaddingTop) cn += " paddingtop"
    if (cell.totalLevel) cn += " total" + cell.totalLevel
    if (cell.isItalic) cn += " italic"
    if (cell.textAlign === "left") cn += " left-align"
    if (cell.textAlign === "right") cn += " right-align"
    if (cell.textAlign === "center") cn += " align-center"
    return cn
  }

  triggerOnSetSheetState = grid => {
    const { sheet, onSetSheetState } = this.props
    if (sheet && onSetSheetState) {
      onSetSheetState({ grid })
      if (grid !== this.state.grid) this.setState({ grid })
    }
  }

  onCellsChanged = changes => {
    const { grid, sheet, locale } = this.state
    const { onSetState, options } = this.props

    if (grid) {
      const patch = {}
      changes.forEach(({ cell, value, row, col }) => {
        const format = cell.format || cell.type
        if (["number", "percentage", "currency"].includes(format)) {
          value = coerceStringToJsNumber(value, locale)
          if (typeof value !== "number") {
            // if we let a string or something else in a Number field we will have "cast to embedded failed" error triggered by Mongoose
            value = NaN
          }
        }
        if (format === "percentage") {
          const valueString = value.toString()
          const decimalCount = (valueString.includes(".") ? valueString.split(".")[1].length || 0 : 0) + 2
          const maxFractionDigits =
            cell.options?.formatPercentageOptions?.maximumFractionDigits || options?.formatPercentageOptions?.maximumFractionDigits || 6
          value = roundAccurately(value / 100, decimalCount < maxFractionDigits ? decimalCount : maxFractionDigits)
        }
        if (format === "date" && value && typeof value === "string") {
          try {
            value = utcParseDate(value, locale)
          } catch (error) {
            value = undefined
          }
        }
        if (row !== undefined && col !== undefined) grid[row][col].value = value
        if (cell.name) patch[cell.name] = value
        if (cell.field && cell.onSetState) cell.onSetState({ [cell.field]: value }) // mode v3: handler at cell level, mimics form input - used by PE commitment
      })
      if (sheet && sheet.values) {
        sheet.values = { ...sheet.values, ...patch }
        this.setState({ sheet })
      }
      this.triggerOnSetSheetState(grid) // mode v1: handler "onSetSheetState()" at sheet level, pass the sheet (aka grid)
      if (onSetState) onSetState(patch) // mode v2: handler "onSetState()" at sheet level, pass an object patch (used by EntityExtension)
    }
  }

  valueViewer = ({ value, cell }) => {
    const { nbColumnsToDisplay } = this.props
    const { grid } = this.state
    let commentComponent
    if (cell.comment) {
      const hasCellOnClick = typeof cell.comment?.onClick === "function"
      const onClick = async event => {
        if (hasCellOnClick) {
          event.stopPropagation()
          await cell.comment.onClick({ value, cell })
        }
      }
      const tooltipText = cell.comment?.text || (typeof cell.comment === "string" ? cell.comment : "")
      const bsStyle = cell.comment?.style || "danger"
      const iconClassName = (hasCellOnClick ? "" : "c-default") + " icn-xxxs " + (cell.comment?.icon || "icn-triangle-corner-top-right")
      const btnClassName = "min-h-fit m-0 pd-0 absolute top-0 right-0 z-10 cell-comment-icon min-w-0"

      commentComponent = tooltipText ? (
        <ButtonWithTooltip bsStyle={bsStyle} className={iconClassName} btnClassName={btnClassName} tooltip={tooltipText} onClick={onClick} />
      ) : (
        <CustomButton simple bsStyle={bsStyle} iconClassName={iconClassName} className={btnClassName} onClick={onClick} />
      )
    }
    const style = { ...(cell?.style || {}) }
    const tableWidth = this.componentRef.current?.offsetWidth
    if (tableWidth && nbColumnsToDisplay && grid?.[0]?.length > nbColumnsToDisplay) {
      style.width = Math.floor(tableWidth / nbColumnsToDisplay - 10)
    }

    return (
      <>
        {commentComponent}
        <div className="value-viewer" style={style}>
          {value}
        </div>
      </>
    )
  }

  valueRenderer = cell => {
    const { dateFormat } = this.state
    const { options, currency } = this.props

    const value = get(cell.obj, cell.field, cell.value)

    try {
      const { locale } = this.state
      if ((cell.format || cell.type) && locale) {
        const format = cell.format || cell.type
        if (format === "number") {
          if (isNaN(value)) return undefined
          return formatDecimal(value, locale, cell.options?.formatNumberOptions || options?.formatNumberOptions)
        } else if (format === "percentage") {
          if (isNaN(value)) return undefined
          return formatPercentage(value, locale, cell.options?.formatPercentageOptions || options?.formatPercentageOptions)
        } else if (format === "currency") {
          if (isNaN(value)) return undefined
          const cellCurrency = cell.currency || currency
          const formatOptions = {
            ...cell.options?.formatCurrencyOptions,
            ...options?.formatCurrencyOptions,
            ...(cellCurrency && { currency: cellCurrency }),
          }
          if (cellCurrency && formatOptions.minimumFractionDigits === undefined && formatOptions.maximumFractionDigits === undefined) {
            const decimals = getCurrencyDecimals(cellCurrency)
            formatOptions.minimumFractionDigits = decimals
            formatOptions.maximumFractionDigits = decimals
          }
          return formatCurrency(value, locale, formatOptions)
        }
      }
      if (value instanceof Date) return moment(value).format(dateFormat)
      if (typeof value === "boolean") return value.toString()

      if (value && typeof value === "object") {
        if (React.isValidElement(value)) return value
        return value?.toString() + " (missing format ???)"
      }
      return value
    } catch (error) {
      console.error("Exception in MathSheetComponent.valueRenderer():", error)
      return value
    }
  }

  dataRenderer = cell => {
    const { dateFormat } = this.state
    if (cell.expr) return cell.expr
    if (cell.format === "percentage") {
      return (cell.value * 100).toFixed(cell.options?.formatPercentageOptions?.maximumFractionDigits || 4)
    }
    if (cell.type === "date" && cell.value instanceof Date) cell.value = moment(cell.value).format(dateFormat)
    return cell.value
  }

  handleCopy = ({ event, data, start, end }) => {
    const { locale } = this.state
    const colMin = start.j
    const colMax = end.j
    const rowMin = start.i
    const rowMax = end.i

    let copiedString = ""

    for (let i = rowMin; i <= rowMax; i++) {
      for (let j = colMin; j <= colMax; j++) {
        const currentCellData = data[i][j] || {}
        const { format, field, type } = currentCellData
        const cellValue = currentCellData.value ?? currentCellData.obj?.[field] ?? ""

        if (format === "number" || type === "currency") {
          copiedString += formatDecimal(cellValue, locale)
        } else if (format === "percentage") {
          copiedString += formatDecimal(cellValue * 100, locale)
        } else {
          copiedString += cellValue
        }

        copiedString += j === colMax ? "" : "\t"
      }

      copiedString += i === rowMax ? "" : "\r\n"
    }

    // we cannot use utils.commons copyToClipboard() because
    // for whatever reason the lib react-datasheet calls this function 2 times very quickly
    // which is prevented by the browser
    if (window.clipboardData && window.clipboardData.setData) {
      window.clipboardData.setData("Text", copiedString)
    } else {
      event.clipboardData.setData("text/plain", copiedString)
    }
  }

  parsePaste = pastedString => {
    if (!pastedString) return [[]]

    const { locale } = this.state
    const rows = pastedString.replace(/\r/g, "").split("\n")
    const grid = []

    if (rows[rows.length - 1] === "") rows.pop()

    for (let i = 0; i < rows.length; i++) {
      const cols = rows[i].split("\t").map(col => coerceStringToJsNumber(col, locale))
      grid.push(cols)
    }
    return grid
  }

  render() {
    const { sheet } = this.props
    const { useStickyHeaders } = sheet || {}
    const { grid } = this.state

    if (!grid) return null

    let sheetRenderer
    if (useStickyHeaders) {
      sheetRenderer = props => {
        // remove the first line because it will be rendered in the thead
        props.children.shift()

        return (
          <table className={props.className}>
            <thead>
              <tr>
                {grid[0]?.map((col, index) => (
                  <th key={index} colSpan={col.colSpan} className={"cell sticky top-0 bg-white " + (col.className || "")}>
                    {col.value}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{props.children}</tbody>
          </table>
        )
      }
    }

    return (
      <div className={"math-sheet-component table-layout-initial" + (useStickyHeaders ? " overflow-visible" : "")} ref={this.componentRef}>
        <Datasheet
          data={grid}
          overflow="clip"
          handleCopy={this.handleCopy}
          parsePaste={this.parsePaste}
          valueViewer={this.valueViewer}
          dataRenderer={this.dataRenderer}
          valueRenderer={this.valueRenderer}
          onCellsChanged={this.onCellsChanged}
          isCellNavigable={cell => !cell.readOnly}
          sheetRenderer={sheetRenderer}
          attributesRenderer={() => undefined} // This is a workaround to force component rendering (for lists)
        />
      </div>
    )
  }
}

export default MathSheetComponent
